<template>
  <div class="sos" :class="{ 'sos--collapsed': collapsed }">
    <!--
      Collapsible header (IT-750). Tagging many brands grew this panel and
      crowded out the tag controls below it, so the whole panel folds to a
      one-line header. The choice is remembered across groups/reloads so an
      operator working a run of stores doesn't have to re-collapse each time.
    -->
    <button
      type="button"
      class="sos__header"
      :aria-expanded="String(!collapsed)"
      @click="toggle"
    >
      <span class="sos__title">Share of shelf</span>
      <span class="sos__meta">
        <span v-if="collapsed && rows.length" class="sos__count">
          {{ rows.length }} {{ rows.length === 1 ? 'brand' : 'brands' }}
        </span>
        <span class="sos__scope">group</span>
        <v-icon class="sos__chevron" small>
          {{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
        </v-icon>
      </span>
    </button>

    <div v-show="!collapsed" class="sos__body">
      <div v-if="!rows.length && !untaggedShare" class="sos__empty text--secondary">
        No boxes yet.
      </div>

      <template v-else>
        <!-- Cap the list height and scroll instead of growing without bound. -->
        <div class="sos__rows">
          <div v-for="row in rows" :key="row.tagId" class="sos__row">
            <div class="sos__bar-track">
              <div class="sos__bar" :style="{width: pct(row.share)}"></div>
            </div>
            <div class="sos__label">
              <span class="sos__name" :title="row.name">{{ row.name }}</span>
              <span class="sos__value">{{ pct(row.share) }}</span>
            </div>
          </div>

          <div v-if="untaggedShare > 0" class="sos__row sos__row--untagged">
            <div class="sos__bar-track">
              <div class="sos__bar sos__bar--untagged" :style="{width: pct(untaggedShare)}"></div>
            </div>
            <div class="sos__label">
              <span class="sos__name">Untagged</span>
              <span class="sos__value">{{ pct(untaggedShare) }}</span>
            </div>
          </div>
        </div>

        <!--
          Shares are per-tag against total shelf width, and a box carrying several
          tags counts its full width toward each. Across independent categories the
          column therefore sums past 100% — say so rather than letting it read as
          an arithmetic bug.
        -->
        <div v-if="exceedsWhole" class="sos__note text--secondary">
          Totals exceed 100% because boxes carry tags from more than one category.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// Remembered collapse preference. Module-level key kept next to the component
// so it is obvious this is a UI preference, not server state.
const COLLAPSE_STORAGE_KEY = 'sosCollapsed'

export default {
  name: 'ShareOfShelfPanel',

  props: {
    shares: {type: Object, default: () => ({})},   // {tagId: fraction}
    untaggedShare: {type: Number, default: 0},
    tagNames: {type: Object, default: () => ({})}, // {tagId: name}
  },

  data() {
    return {
      collapsed: false,
    }
  },

  created() {
    // localStorage can throw (private mode / disabled); default to expanded.
    try {
      this.collapsed = localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1'
    } catch (e) {
      this.collapsed = false
    }
  },

  computed: {
    rows() {
      return Object.keys(this.shares)
        .map(tagId => ({
          tagId,
          name: this.tagNames[tagId] || `Tag ${tagId}`,
          share: this.shares[tagId],
        }))
        .sort((a, b) => b.share - a.share)
    },

    exceedsWhole() {
      const total = this.rows.reduce((sum, row) => sum + row.share, 0)
      return total > 1.0001
    },
  },

  methods: {
    toggle() {
      this.collapsed = !this.collapsed
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, this.collapsed ? '1' : '0')
      } catch (e) {
        // preference just won't persist; in-session toggle still works
      }
    },

    pct(fraction) {
      return `${Math.round(fraction * 100)}%`
    },
  },
}
</script>

<style scoped>
.sos {
  font-size: 12px;
}

/* The header is now a full-width toggle button; strip the native button chrome
   so it keeps the original label look. */
.sos__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 8px;
  padding: 2px 0;
  background: none;
  border: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
}

.sos__header:hover .sos__title {
  color: rgba(0, 0, 0, 0.75);
}

.sos__header:focus-visible {
  outline: 2px solid var(--v-primary-base, #ff6a03);
  outline-offset: 2px;
}

.sos--collapsed .sos__header {
  margin-bottom: 0;
}

.sos__title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.55);
}

.sos__meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sos__count {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.5);
  font-variant-numeric: tabular-nums;
}

.sos__scope {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 3px;
  padding: 0 4px;
}

.sos__chevron {
  color: rgba(0, 0, 0, 0.45) !important;
}

/* Cap the bar list and scroll so a store with many brands can't push the tag
   controls below it off-screen (IT-750). */
.sos__rows {
  max-height: 34vh;
  overflow-y: auto;
  padding-right: 2px;
}

.sos__row {
  margin-bottom: 6px;
}

.sos__bar-track {
  height: 6px;
  background: rgba(0, 0, 0, 0.07);
  border-radius: 3px;
  overflow: hidden;
}

.sos__bar {
  height: 100%;
  background: #1e88e5;
  border-radius: 3px;
  transition: width 0.18s ease;
}

.sos__bar--untagged {
  background: #bdbdbd;
}

.sos__label {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}

.sos__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sos__value {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.6);
}

.sos__row--untagged .sos__name,
.sos__row--untagged .sos__value {
  color: rgba(0, 0, 0, 0.5);
}

.sos__empty,
.sos__note {
  font-size: 11px;
  line-height: 1.4;
  margin-top: 8px;
}

/* Dark mode (restyle step 5): every grey here is an rgba(0,0,0,..) tint that
   vanishes on a dark surface - flip them to white alphas. The bars (#1e88e5 /
   #bdbdbd) read fine on dark and are part of the shared encoding, so leave
   them. .theme--dark is the whole-app dark class on the v-app ancestor. */
.theme--dark .sos__header:hover .sos__title { color: rgba(255, 255, 255, 0.85); }
.theme--dark .sos__title { color: rgba(255, 255, 255, 0.6); }
.theme--dark .sos__count { color: rgba(255, 255, 255, 0.55); }
.theme--dark .sos__scope { color: rgba(255, 255, 255, 0.5); border-color: rgba(255, 255, 255, 0.24); }
.theme--dark .sos__chevron { color: rgba(255, 255, 255, 0.5) !important; }
.theme--dark .sos__bar-track { background: rgba(255, 255, 255, 0.12); }
.theme--dark .sos__value { color: rgba(255, 255, 255, 0.7); }
.theme--dark .sos__row--untagged .sos__name,
.theme--dark .sos__row--untagged .sos__value { color: rgba(255, 255, 255, 0.55); }
</style>
